import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './AnalyticsPage.css';
import AcquisitionPage from './AcquisitionPage';
import Overview from './cockpit/Overview';
import People from './cockpit/People';
import Videos from './cockpit/Videos';
import Landings from './cockpit/Landings';
import LiveGlobe from './cockpit/LiveGlobe';

/**
 * Cockpit OpenBro (/admin/analytics). Tout vient du Worker (netlify-proxy/worker.js, objet AnalyticsLog) :
 * GET /api/analytics/funnel toutes les 20 s, GET /api/analytics/replay?sid= pour un dossier.
 * Même jeton qu'avant (sessionStorage openbro_analytics_token = secret Worker ANALYTICS_TOKEN).
 */

// Même origine : la page et l'API sont servies par le Worker (marche aussi avec `wrangler dev`).
const API = '';
const TOKEN_KEY = 'openbro_analytics_token';

const NAV = [
  { id: 'apercu', label: 'Vue d’ensemble', icon: 'M4 13h6V4H4zm10 7h6V4h-6zM4 20h6v-4H4z' },
  { id: 'personnes', label: 'Personnes', icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 8a7 7 0 0 1 14 0z' },
  { id: 'videos', label: 'Vidéos', icon: 'M4 6h11v12H4zm11 4 5-3v10l-5-3z' },
  { id: 'acquisition', label: 'Acquisition', icon: 'M4 18 10 12l4 4 6-8M15 8h5v5' },
];
const PERIODS = [['24h', '24 h'], ['7j', '7 jours'], ['30j', '30 jours']];
const TITLES = { apercu: 'Vue d’ensemble', personnes: 'Personnes', videos: 'Vidéos', acquisition: 'Acquisition' };

/** Si le globe casse, le reste du cockpit reste ouvert. */
class GlobeGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { down: false };
  }

  static getDerivedStateFromError() {
    return { down: true };
  }

  render() {
    if (this.state.down) return null;
    return this.props.children;
  }
}

function sectionFrom(asked) {
  if (asked === 'replays') return 'personnes';
  return NAV.some((item) => item.id === asked) ? asked : 'apercu';
}

export default function AnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const section = sectionFrom(params.get('section'));
  const selected = params.get('sid') || '';
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [draft, setDraft] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [period, setPeriod] = useState('24h');
  // Cet appareil est-il exclu des stats (cookie ob_internal, posé par le Worker) ?
  const [internal, setInternal] = useState(null);

  useEffect(() => {
    fetch('/api/analytics/internal', { headers: { accept: 'application/json' } })
      .then((res) => res.json())
      .then((body) => setInternal(Boolean(body.internal)))
      .catch(() => setInternal(null));
  }, []);

  const toggleInternal = async () => {
    try {
      const res = await fetch('/api/analytics/internal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ on: !internal }),
      });
      const body = await res.json();
      if (res.ok) setInternal(Boolean(body.internal));
    } catch { /* on garde l'état affiché */ }
  };

  const purgeOwnerHistory = async () => {
    try {
      const res = await fetch('/api/analytics/purge-owner', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) window.location.reload();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!token) return undefined;
    let stop = false;
    let busy = false;
    const load = async () => {
      if (busy) return;
      busy = true;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 20000);
      try {
        const res = await fetch(`${API}/api/analytics/funnel`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
        const body = await res.json();
        if (stop) return;
        if (!res.ok) {
          setError(body.detail || 'Accès refusé');
          if (res.status === 401) setData(null);
          return;
        }
        setError('');
        setData(body);
        setLoadedAt(Date.now());
      } catch {
        if (!stop) setError('Le Worker ne répond pas. Nouvel essai dans 20 s.');
      } finally {
        busy = false;
        window.clearTimeout(timer);
      }
    };
    load();
    const poll = window.setInterval(load, 20000);
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      stop = true;
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [token]);

  const go = useCallback((id, sid) => {
    const next = {};
    if (id !== 'apercu') next.section = id;
    if (sid) next.sid = sid;
    setParams(next);
  }, [setParams]);
  const openPerson = useCallback((sid) => go('personnes', sid), [go]);
  const selectPerson = useCallback((sid) => setParams(sid ? { section: 'personnes', sid } : { section: 'personnes' }, { replace: true }), [setParams]);

  const unlock = (event) => {
    event.preventDefault();
    const next = draft.trim();
    if (!next) return;
    sessionStorage.setItem(TOKEN_KEY, next);
    setToken(next);
  };
  const lock = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setData(null);
  };

  if (!token) {
    return (
      <main className="ck ck-lock">
        <form onSubmit={unlock}>
          <p className="ck-brand">OpenBro <span>Cockpit</span></p>
          <input value={draft} onChange={(event) => setDraft(event.target.value)} type="password" placeholder="Jeton d’accès" autoFocus />
          <button type="submit" className="ck-btn is-primary">Ouvrir</button>
        </form>
      </main>
    );
  }

  const liveCount = (data?.live || []).length;
  const ago = loadedAt ? Math.max(0, Math.round((now - loadedAt) / 1000)) : null;

  return (
    <main className="ck">
      <aside className="ck-side">
        <p className="ck-brand">OpenBro <span>Cockpit</span></p>
        <nav>
          {NAV.map((item) => (
            <button key={item.id} type="button" className={section === item.id ? 'is-on' : ''} onClick={() => go(item.id)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d={item.icon} /></svg>
              {item.label}
              {item.id === 'personnes' && liveCount > 0 && <em className="ck-live-count"><span className="ck-dot is-live" />{liveCount}</em>}
            </button>
          ))}
        </nav>
        <GlobeGuard>
          <LiveGlobe live={data?.live || []} onOpen={() => go('personnes')} />
        </GlobeGuard>
        <div className="ck-side-foot">
          <p>{ago === null ? 'Chargement…' : `Mis à jour il y a ${ago} s`}</p>
          <button type="button" className={internal ? 'ck-internal is-on' : 'ck-internal'} onClick={toggleInternal} disabled={internal === null}
            title="Exclut tout ce que fait ce navigateur des stats (visites, clics, checkout, achats). À faire sur chaque téléphone et ordinateur de Gaut.">
            {internal ? 'Cet appareil : exclu des stats' : 'Exclure cet appareil des stats'}
          </button>
          <button type="button" className="ck-internal" onClick={purgeOwnerHistory} disabled={!token}
            title="Retire de l’historique les visites Nice/Antibes, ton IP box et les sessions déjà marquées « toi ».">
            Purger mon historique des stats
          </button>
          <button type="button" onClick={lock}>Fermer la session</button>
        </div>
      </aside>
      <div className="ck-main">
        <header className="ck-top">
          <h1>{TITLES[section]}</h1>
          {section === 'apercu' && (
            <div className="ck-seg" role="tablist" aria-label="Période">
              {PERIODS.map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={period === id} className={period === id ? 'is-on' : ''} onClick={() => setPeriod(id)}>{label}</button>
              ))}
            </div>
          )}
        </header>
        {error && <p className="ck-error">{error}</p>}
        {section === 'acquisition' && (
          <div className="ck-view ck-acq">
            <Landings token={token} />
            <AcquisitionPage embedded />
          </div>
        )}
        {section !== 'acquisition' && !data && !error && <p className="ck-empty">Chargement…</p>}
        {data && section === 'apercu' && <Overview data={data} period={period} now={now} openPerson={openPerson} />}
        {data && section === 'personnes' && (
          <People people={data.people || []} token={token} now={now} selected={selected} onSelect={selectPerson} activitySince={data.videos?.since || null} />
        )}
        {data && section === 'videos' && <Videos videos={data.videos} />}
      </div>
    </main>
  );
}
